/**
 * Iter121–122 · Licencias internas de manejo (SGSSO)
 * ---------------------------------------------------------------
 * Emite tarjetas tipo CR80 (85.6 × 54 mm) vinculadas al empleado en HR.
 * Las certificaciones vigentes se toman como snapshot al momento de crear.
 *
 * Iter122 · Foto capturada al vuelo + panel de diseño (logo + colores)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  IdentificationCard, Printer, Plus, PencilSimple, Trash,
  Camera, Upload, Palette, X, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";

const EQ_OPTIONS = ["Jumbo", "Voladuras", "Anclador", "Scoop", "Camión", "Retro", "Telehandler", "Motoniveladora"];
const CLASSES = [
  { v: "A", l: "A · Ligero personal" },
  { v: "B", l: "B · Vehículo utilitario / camioneta" },
  { v: "C", l: "C · Camión / equipo pesado" },
  { v: "D", l: "D · Maquinaria pesada / minería" },
  { v: "E", l: "E · Especial (voladuras / anclaje)" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_DESIGN = {
  logo_data_url: "",
  primary_color: "#0b2545",
  accent_color: "#c8a24b",
  text_color: "#ffffff",
  subtitle: "LICENCIA INTERNA DE MANEJO · SGSSO",
  footer_note: "Documento de identificación interna. Válido dentro de operaciones.",
};

export default function DriverLicensesModule() {
  const { has } = usePermissions();
  const canWrite = has("hse.write");
  const [licenses, setLicenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [scale, setScale] = useState(1.0);
  const [dlg, setDlg] = useState(null);
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [designOpen, setDesignOpen] = useState(false);

  const loadDesign = useCallback(async () => {
    try {
      const { data } = await api.get("/hr/driver-licenses/design");
      setDesign({ ...DEFAULT_DESIGN, ...(data || {}) });
    } catch { /* usa defaults */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const [{ data: lic }, { data: emps }] = await Promise.all([
        api.get(`/hr/driver-licenses${q ? `?q=${encodeURIComponent(q)}` : ""}`),
        api.get("/hr/employees?status=active"),
      ]);
      setLicenses(lic || []);
      setEmployees(emps || []);
    } catch (e) { toast.error(apiError(e)); }
  }, [q]);

  useEffect(() => { load(); loadDesign(); }, [load, loadDesign]);

  const empById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

  const openNew = () => setDlg({ mode: "new", entry: {
    employee_id: "", license_number: "", license_class: "D",
    issued_date: new Date().toISOString().slice(0, 10),
    expires_date: new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toISOString().slice(0, 10),
    restrictions: "", equipment_authorized: [], notes: "", photo_data_url: "",
  } });
  const openEdit = (l) => setDlg({ mode: "edit", entry: { ...l, photo_data_url: "" } });

  const del = async (l) => {
    if (!window.confirm(`¿Eliminar la licencia de ${l.employee_name}?`)) return;
    try { await api.delete(`/hr/driver-licenses/${l.id}`); load(); toast.success("Licencia eliminada"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="driver-licenses-module">
      <div className="flex flex-wrap items-center gap-2">
        <IdentificationCard size={20} weight="fill" className="text-blue-800" />
        <div>
          <h3 className="font-heading font-black text-lg text-slate-900">Licencias de manejo</h3>
          <p className="text-xs text-slate-500">Emisión de tarjeta interna CR80 con logo, foto y certificados vinculados a HR.</p>
        </div>
        <div className="flex-1" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, DNI o folio…"
          className="h-9 w-64" data-testid="dl-search" />
        <div>
          <Label className="text-[10px] uppercase text-slate-500">Escala PDF</Label>
          <Select value={String(scale)} onValueChange={(v) => setScale(Number(v))}>
            <SelectTrigger className="h-9 w-32" data-testid="dl-scale"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="0.5">50 %</SelectItem>
              <SelectItem value="0.75">75 %</SelectItem>
              <SelectItem value="1">100 % (real)</SelectItem>
              <SelectItem value="1.25">125 %</SelectItem>
              <SelectItem value="1.5">150 %</SelectItem>
              <SelectItem value="2">200 %</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canWrite && (
          <>
            <Button variant="outline" onClick={() => setDesignOpen(true)}
              className="gap-1.5 h-9 mt-4 border-slate-300" data-testid="dl-design-open">
              <Palette size={14} weight="bold" /> Diseño
            </Button>
            <Button onClick={openNew} className="bg-blue-800 hover:bg-blue-700 text-white gap-1.5 h-9 mt-4" data-testid="dl-new">
              <Plus size={14} weight="bold" /> Nueva licencia
            </Button>
          </>
        )}
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-xs" data-testid="dl-table">
          <thead className="bg-slate-100 text-slate-700 uppercase text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Empleado</th>
              <th className="px-2 py-2 text-left">Folio</th>
              <th className="px-2 py-2 text-center">Cat.</th>
              <th className="px-2 py-2 text-center">Emitida</th>
              <th className="px-2 py-2 text-center">Vence</th>
              <th className="px-2 py-2 text-left">Equipos autorizados</th>
              <th className="px-2 py-2 text-center">Cert.</th>
              <th className="px-2 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {licenses.length === 0 && (
              <tr><td colSpan={8} className="text-center py-4 text-slate-400 italic">Sin licencias emitidas. Crea la primera con «Nueva licencia».</td></tr>
            )}
            {licenses.map((l) => {
              const nowIso = new Date().toISOString().slice(0, 10);
              const expired = l.expires_date && l.expires_date < nowIso;
              return (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`dl-row-${l.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {l.photo_url?.startsWith("data:image") ? (
                        <img src={l.photo_url} alt="" className="w-8 h-10 object-cover rounded border border-slate-200" />
                      ) : (
                        <div className="w-8 h-10 rounded border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-slate-400">
                          <Camera size={12} />
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-slate-900">{l.employee_name}</div>
                        <div className="text-[10px] text-slate-500">{l.dni} · {l.position}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 font-mono text-slate-800">{l.license_number}</td>
                  <td className="px-2 py-2 text-center"><span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-900 font-bold">{l.license_class}</span></td>
                  <td className="px-2 py-2 text-center text-slate-600">{l.issued_date || "—"}</td>
                  <td className={`px-2 py-2 text-center ${expired ? "text-rose-600 font-bold" : "text-slate-600"}`}>{l.expires_date || "—"}{expired && " ⚠"}</td>
                  <td className="px-2 py-2 text-slate-700">{(l.equipment_authorized || []).join(", ") || "—"}</td>
                  <td className="px-2 py-2 text-center text-slate-500">{(l.certifications_snapshot || []).length}</td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <a href={`${BACKEND}/api/hr/driver-licenses/${l.id}/pdf?scale=${scale}`}
                       target="_blank" rel="noopener noreferrer"
                       className="text-blue-700 hover:text-blue-900 text-[11px] font-bold mr-2"
                       data-testid={`dl-pdf-${l.id}`}>
                      <Printer size={12} weight="bold" className="inline mr-0.5" />PDF
                    </a>
                    {canWrite && (
                      <>
                        <button onClick={() => openEdit(l)} className="text-slate-500 hover:text-amber-600 mr-1" data-testid={`dl-edit-${l.id}`}><PencilSimple size={12} weight="bold" /></button>
                        <button onClick={() => del(l)} className="text-slate-500 hover:text-rose-600" data-testid={`dl-del-${l.id}`}><Trash size={12} weight="bold" /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {dlg && <LicenseDialog dlg={dlg} employees={employees} empById={empById}
                              design={design}
                              onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
      {designOpen && <DesignPanel value={design} onClose={() => setDesignOpen(false)}
                                    onSaved={(next) => { setDesign(next); setDesignOpen(false); toast.success("Diseño actualizado"); }} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* PhotoPicker: cámara + subir archivo + preview                       */
/* ------------------------------------------------------------------ */
function PhotoPicker({ value, fallback, onChange }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);
  const [active, setActive] = useState(false);
  const [err, setErr] = useState("");

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setActive(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const start = async () => {
    setErr("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Cámara no disponible en este navegador.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 640 } },
        audio: false,
      });
      streamRef.current = stream;
      setActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 60);
    } catch (e) {
      setErr(e.name === "NotAllowedError"
        ? "Permiso denegado — activa la cámara en el navegador."
        : "No se pudo abrir la cámara.");
    }
  };

  const snap = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 480;
    const h = v.videoHeight || 640;
    // Recortamos a proporción retrato tipo credencial (3:4)
    const targetRatio = 3 / 4;
    let cropW = w, cropH = h;
    if (w / h > targetRatio) { cropW = Math.floor(h * targetRatio); }
    else { cropH = Math.floor(w / targetRatio); }
    const sx = Math.floor((w - cropW) / 2);
    const sy = Math.floor((h - cropH) / 2);
    c.width = 480;
    c.height = 640;
    const ctx = c.getContext("2d");
    ctx.translate(c.width, 0); ctx.scale(-1, 1);
    ctx.drawImage(v, sx, sy, cropW, cropH, 0, 0, c.width, c.height);
    const url = c.toDataURL("image/jpeg", 0.78);
    onChange(url);
    stop();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Debe ser imagen"); return; }
    if (f.size > 3_000_000) { toast.error("Máx 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.readAsDataURL(f);
  };

  const currentSrc = value || fallback || "";

  return (
    <div className="border border-slate-200 rounded-lg p-2 bg-white space-y-2" data-testid="dl-photo-picker">
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
      {active ? (
        <div className="relative bg-slate-900 rounded overflow-hidden" style={{ aspectRatio: "3/4" }}>
          <video ref={videoRef} playsInline muted className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
          <div className="absolute inset-0 border-2 border-blue-400/50 pointer-events-none rounded" />
          <button type="button" onClick={stop}
                   className="absolute top-1.5 right-1.5 bg-white/85 rounded-full p-1"
                   data-testid="dl-photo-cancel"><X size={14} /></button>
        </div>
      ) : currentSrc ? (
        <div className="relative">
          <img src={currentSrc} alt="Foto empleado"
               className="w-full rounded border border-slate-200"
               style={{ aspectRatio: "3/4", objectFit: "cover" }} />
          {value && (
            <span className="absolute bottom-1 left-1 bg-emerald-600 text-white text-[9px] px-1.5 py-0.5 rounded font-bold">
              Nueva
            </span>
          )}
        </div>
      ) : (
        <div className="rounded border border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400 text-xs gap-1"
             style={{ aspectRatio: "3/4" }}>
          <Camera size={22} />
          <span>Sin foto</span>
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />

      {active ? (
        <Button type="button" onClick={snap}
                className="w-full bg-blue-800 hover:bg-blue-700 text-white h-8 text-xs gap-1"
                data-testid="dl-photo-snap">
          <Camera size={12} weight="bold" /> Capturar
        </Button>
      ) : (
        <div className="flex gap-1.5">
          <Button type="button" onClick={start} variant="outline"
                  className="flex-1 h-8 text-xs gap-1" data-testid="dl-photo-camera">
            <Camera size={12} weight="bold" /> Cámara
          </Button>
          <Button type="button" onClick={() => fileRef.current?.click()} variant="outline"
                  className="flex-1 h-8 text-xs gap-1" data-testid="dl-photo-upload">
            <Upload size={12} weight="bold" /> Subir
          </Button>
          {value && (
            <Button type="button" onClick={() => onChange("")} variant="outline"
                    className="h-8 text-xs gap-1 text-rose-600 border-rose-200"
                    data-testid="dl-photo-reset" title="Descartar foto capturada">
              <ArrowCounterClockwise size={12} weight="bold" />
            </Button>
          )}
        </div>
      )}
      {err && <p className="text-[10px] text-rose-600">{err}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* LivePreview: mini tarjeta que refleja el diseño actual              */
/* ------------------------------------------------------------------ */
function LivePreview({ design, entry, emp, photoSrc }) {
  const primary = design.primary_color || "#0b2545";
  const accent = design.accent_color || "#c8a24b";
  const text = design.text_color || "#ffffff";
  return (
    <div className="rounded-md shadow-md overflow-hidden border" style={{ borderColor: primary, width: 300 }}
         data-testid="dl-preview">
      <div className="flex items-center gap-2 px-2 py-1.5"
           style={{ background: primary, color: text }}>
        {design.logo_data_url ? (
          <img src={design.logo_data_url} alt="" className="w-7 h-7 object-contain bg-white/10 rounded p-0.5" />
        ) : (
          <div className="w-7 h-7 rounded bg-white/10 flex items-center justify-center text-[9px]">LOGO</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black leading-tight truncate">MINERA OOMINING</div>
          <div className="text-[7px] opacity-80 leading-tight truncate">{design.subtitle}</div>
        </div>
        <div className="w-8 h-8 rounded flex items-center justify-center text-sm font-black"
             style={{ background: accent, color: text }}>
          {entry.license_class || "—"}
        </div>
      </div>
      <div className="flex gap-2 p-2 bg-white">
        <div className="w-16 h-20 rounded border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
          {photoSrc ? <img src={photoSrc} alt="" className="w-full h-full object-cover" />
                     : <span className="text-[8px] text-slate-400">Sin foto</span>}
        </div>
        <div className="flex-1 text-[8.5px] leading-tight space-y-0.5">
          <div className="text-slate-400 uppercase font-bold">Nombre</div>
          <div className="font-black text-slate-900 truncate">{emp?.name || "—"}</div>
          <div className="text-slate-400 uppercase font-bold mt-0.5">DNI · Puesto</div>
          <div className="text-slate-700 truncate">{emp?.dni || "—"} · {emp?.position || "—"}</div>
          <div className="text-slate-400 uppercase font-bold mt-0.5">Folio</div>
          <div className="font-mono text-slate-900 truncate">{entry.license_number || "AUTO"}</div>
          <div className="text-slate-400 uppercase font-bold mt-0.5">Vigencia</div>
          <div className="text-slate-700">{entry.issued_date || "—"} → {entry.expires_date || "—"}</div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* LicenseDialog                                                       */
/* ------------------------------------------------------------------ */
function LicenseDialog({ dlg, employees, empById, design, onClose, onSaved }) {
  const [f, setF] = useState(dlg.entry);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleEq = (v) => setF((s) => {
    const cur = new Set(s.equipment_authorized || []);
    if (cur.has(v)) cur.delete(v); else cur.add(v);
    return { ...s, equipment_authorized: [...cur] };
  });
  const emp = empById[f.employee_id];
  const certPreview = emp?.certifications || [];
  // Foto mostrada en preview: la nueva si el usuario capturó, si no la existente (edición)
  // o la del empleado en HR.
  const photoSrc = f.photo_data_url || f.photo_url || emp?.photo_url || "";

  const save = async () => {
    if (!f.employee_id) { toast.error("Selecciona un empleado"); return; }
    setBusy(true);
    try {
      const payload = {
        employee_id: f.employee_id,
        license_number: f.license_number || "",
        license_class: f.license_class || "D",
        issued_date: f.issued_date || "",
        expires_date: f.expires_date || "",
        restrictions: f.restrictions || "",
        equipment_authorized: f.equipment_authorized || [],
        notes: f.notes || "",
        photo_data_url: f.photo_data_url || "",
      };
      if (dlg.mode === "edit") await api.put(`/hr/driver-licenses/${f.id}`, payload);
      else await api.post("/hr/driver-licenses", payload);
      toast.success("Licencia guardada");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white text-slate-900 max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-blue-900 flex items-center gap-2">
            <IdentificationCard size={20} weight="fill" />
            {dlg.mode === "edit" ? "Editar" : "Nueva"} licencia de manejo
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
          {/* Columna izquierda: FOTO + preview */}
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Foto para la licencia</Label>
              <PhotoPicker value={f.photo_data_url}
                            fallback={f.photo_url || emp?.photo_url || ""}
                            onChange={(v) => upd("photo_data_url", v)} />
              <p className="text-[10px] text-slate-500 mt-1 italic">
                Sin foto nueva → se usa la del empleado en HR ({emp?.photo_url ? "disponible" : "no registrada"}).
              </p>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Vista previa</Label>
              <LivePreview design={design} entry={f} emp={emp} photoSrc={photoSrc} />
            </div>
          </div>

          {/* Columna derecha: DATOS */}
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Empleado *</Label>
              <Select value={f.employee_id} onValueChange={(v) => upd("employee_id", v)}>
                <SelectTrigger className="bg-white" data-testid="dl-form-employee"><SelectValue placeholder="Selecciona un operador…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name} — {e.position || "—"} ({e.dni})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-slate-500">N° Licencia</Label>
                <Input value={f.license_number} onChange={(e) => upd("license_number", e.target.value)}
                  placeholder="Auto: LM-{DNI}" data-testid="dl-form-num" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Categoría</Label>
                <Select value={f.license_class} onValueChange={(v) => upd("license_class", v)}>
                  <SelectTrigger className="bg-white" data-testid="dl-form-class"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {CLASSES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Tipo sangre</Label>
                <Input value={emp?.blood_type || ""} disabled className="bg-slate-50" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Emitida</Label>
                <Input type="date" value={f.issued_date} onChange={(e) => upd("issued_date", e.target.value)} data-testid="dl-form-issued" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Vence</Label>
                <Input type="date" value={f.expires_date} onChange={(e) => upd("expires_date", e.target.value)} data-testid="dl-form-expires" />
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase text-slate-500">Equipos autorizados</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {EQ_OPTIONS.map((eq) => {
                  const active = (f.equipment_authorized || []).includes(eq);
                  return (
                    <button type="button" key={eq} onClick={() => toggleEq(eq)}
                      data-testid={`dl-form-eq-${eq}`}
                      className={`text-[11px] px-2 py-1 rounded border ${active ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                      {eq}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase text-slate-500">Restricciones</Label>
              <Input value={f.restrictions} onChange={(e) => upd("restrictions", e.target.value)}
                placeholder="Ej: uso obligatorio de lentes, sin voladuras nocturnas…" data-testid="dl-form-restrictions" />
            </div>

            {emp && (
              <div className="border border-emerald-200 bg-emerald-50/50 rounded p-2 space-y-1"
                   data-testid="dl-form-cert-preview">
                <p className="text-[10px] uppercase text-emerald-800 font-bold">
                  Certificaciones que se anclarán a esta licencia · {certPreview.length}
                </p>
                {certPreview.length === 0 ? (
                  <p className="text-[11px] italic text-slate-500">El empleado no tiene certificaciones registradas en HR. Agrégale certificados desde el módulo Nómina para que aparezcan aquí.</p>
                ) : (
                  <ul className="text-[10.5px] space-y-0.5">
                    {certPreview.slice(0, 8).map((c, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span>• {c.name} <span className="text-slate-500">({c.issuer || "—"})</span></span>
                        <span className="font-mono text-slate-600">{c.expires_date || "sin fecha"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-800 hover:bg-blue-700 text-white" data-testid="dl-form-save">
            {busy ? "Guardando…" : "Guardar licencia"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* DesignPanel · Logo global + paleta de colores                       */
/* ------------------------------------------------------------------ */
function DesignPanel({ value, onClose, onSaved }) {
  const [d, setD] = useState({ ...DEFAULT_DESIGN, ...value });
  const [busy, setBusy] = useState(false);
  const logoRef = useRef(null);
  const upd = (k, v) => setD((s) => ({ ...s, [k]: v }));

  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Debe ser imagen"); return; }
    if (f.size > 900_000) { toast.error("Máx ~650 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => upd("logo_data_url", String(reader.result));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/hr/driver-licenses/design", d);
      onSaved({ ...DEFAULT_DESIGN, ...data });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const reset = () => setD(DEFAULT_DESIGN);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white text-slate-900 max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-blue-900 flex items-center gap-2">
            <Palette size={20} weight="fill" /> Diseño de las licencias
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Configuración */}
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Logo institucional (PNG/JPG/WEBP · máx 650 KB)</Label>
              <input ref={logoRef} type="file" accept="image/*" onChange={onLogoFile}
                     className="hidden" data-testid="dl-design-logo-input" />
              <div className="flex items-center gap-2 mt-1">
                <div className="w-16 h-16 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                  {d.logo_data_url
                    ? <img src={d.logo_data_url} alt="logo" className="w-full h-full object-contain" />
                    : <span className="text-[9px] text-slate-400">Sin logo</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <Button type="button" size="sm" variant="outline"
                          onClick={() => logoRef.current?.click()}
                          className="gap-1 h-8 text-xs" data-testid="dl-design-logo-upload">
                    <Upload size={12} /> Subir logo
                  </Button>
                  {d.logo_data_url && (
                    <Button type="button" size="sm" variant="outline"
                            onClick={() => upd("logo_data_url", "")}
                            className="gap-1 h-8 text-xs text-rose-600 border-rose-200"
                            data-testid="dl-design-logo-clear">
                      <Trash size={12} /> Quitar
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <ColorField label="Color primario (banda superior)" value={d.primary_color}
                        onChange={(v) => upd("primary_color", v)} testid="dl-design-primary" />
            <ColorField label="Color de acento (chip categoría / detalles)" value={d.accent_color}
                        onChange={(v) => upd("accent_color", v)} testid="dl-design-accent" />
            <ColorField label="Color de texto sobre banda" value={d.text_color}
                        onChange={(v) => upd("text_color", v)} testid="dl-design-text" />

            <div>
              <Label className="text-[10px] uppercase text-slate-500">Subtítulo del encabezado</Label>
              <Input value={d.subtitle} onChange={(e) => upd("subtitle", e.target.value)}
                     maxLength={120} data-testid="dl-design-subtitle" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Nota legal · pie de tarjeta</Label>
              <Input value={d.footer_note} onChange={(e) => upd("footer_note", e.target.value)}
                     maxLength={280} data-testid="dl-design-footer" />
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label className="text-[10px] uppercase text-slate-500">Vista previa (anverso)</Label>
            <div className="flex flex-col items-center gap-3 p-3 bg-slate-50 rounded-md border border-slate-200">
              <LivePreview design={d}
                            entry={{ license_class: "D", license_number: "LM-EJEMPLO", issued_date: "2026-02-16", expires_date: "2028-02-16" }}
                            emp={{ name: "Juan Ejemplo Ríos", dni: "12345678", position: "Operador Jumbo" }}
                            photoSrc="" />
              <p className="text-[10px] text-slate-500 italic text-center">
                Cambios aplican a todas las licencias emitidas. El PDF se re-genera al descargarlo.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <Button variant="outline" onClick={reset} className="gap-1 text-xs"
                  data-testid="dl-design-reset">
            <ArrowCounterClockwise size={12} /> Restablecer defaults
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
            <Button onClick={save} disabled={busy}
                    className="bg-blue-800 hover:bg-blue-700 text-white"
                    data-testid="dl-design-save">
              {busy ? "Guardando…" : "Guardar diseño"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase text-slate-500">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" value={value || "#000000"}
               onChange={(e) => onChange(e.target.value)}
               className="w-10 h-9 rounded border border-slate-200 cursor-pointer"
               data-testid={`${testid}-swatch`} />
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
               className="font-mono text-xs w-32" placeholder="#RRGGBB"
               data-testid={`${testid}-hex`} />
      </div>
    </div>
  );
}
